import React from 'react'
import styled from 'styled-components'

import { mobile } from './media-query'

const Container = styled.div`
  width: 100%;
`

const IconsContainer = styled.div`
  display: flex;
  justify-content: space-around;
  margin-bottom: 27px;
  width: 100%;

  @media ${mobile.query} {
    display: none;
  }
`

const Icon = styled.div<{ iconSrc?: string }>`
  cursor: pointer;
  background-image: ${({ iconSrc }) => (iconSrc ? `url(${iconSrc})` : undefined)};
  background-position: center;
  background-repeat: no-repeat;
  height: 36px;
  width: 36px;
`

const ButtonsContainer = styled.div`
  background-color: rgba(255, 255, 255, 0.25);
  border-radius: 41px;
  display: flex;
  height: 58px;
  margin-bottom: 15px;
  position: relative;

  @media ${mobile.query} {
    background-color: ${({ theme }) => theme.palette.giphyDarkGrey};
    border-radius: 21px;
    height: 40px;
  }
`

const ToggleSelection = styled.span`
  background-color: black;
  border-radius: 41px;
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  transition: left 200ms ease-out, background-position 200ms ease-out;
  width: calc(50% + 5px);

  @media ${mobile.query} {
    background: ${({ theme }) => `linear-gradient(to right, #00b8bd, ${theme.palette.giphyGreen})`};
    border-radius: 21px;
  }
`

const Label = styled.label`
  align-items: center;
  box-sizing: border-box;
  color: ${({ theme }) => theme.palette.giphyWhite};
  cursor: pointer;
  display: flex;
  font-family: PTMono, monospace;
  font-size: 24px;
  font-weight: bold;
  justify-content: center;
  line-height: 30px;
  user-select: none;
  width: 50%;
  z-index: 1;

  &:first-of-type {
    padding-left: 11px;
  }

  &:last-of-type {
    padding-right: 11px;
  }

  @media ${mobile.query} {
    opacity: 0.66;
    font-size: 18px;
  }
`

const Input = styled.input`
  display: none;

  &:checked {
    + ${Label} {
      width: calc(50% + 11px);
      transition: color 200ms ease-out;
    }

    + ${Label}:first-of-type {
      color: ${({ theme }) => theme.palette.giphyLightBlue};
      padding-left: 0;
    }

    + ${Label}:last-of-type {
      color: ${({ theme }) => theme.palette.giphyBlue};
      padding-right: 0;

      ~ ${ToggleSelection} {
        left: calc(50% - 5px);
      }
    }

    @media ${mobile.query} {
      && + ${Label} {
        color: ${({ theme }) => theme.palette.giphyBlack};
        opacity: 1;
      }
    }
  }
`

type ToggleProps = {
  icon1?: string
  icon2?: string
  name: string
  onChange: (value: string) => void
  option1: string
  option2: string
  value: string
}

export const Toggle: React.FC<ToggleProps> = (props) => {
  const { option1, option2, icon1, icon2, onChange, value, name } = props

  return (
    <Container>
      {(icon1 || icon2) && (
        <IconsContainer>
          <Icon iconSrc={icon1} onClick={() => onChange(option1)} />
          <Icon iconSrc={icon2} onClick={() => onChange(option2)} />
        </IconsContainer>
      )}
      <ButtonsContainer>
        <Input
          type="radio"
          checked={value === option1}
          id={option1}
          value={option1}
          name={name}
          onChange={(e) => onChange(e.target.value)}
        />
        <Label htmlFor={option1}>
          <span>{option1}</span>
        </Label>
        <Input
          type="radio"
          checked={value === option2}
          id={option2}
          value={option2}
          name={name}
          onChange={(e) => onChange(e.target.value)}
        />
        <Label htmlFor={option2}>
          <span>{option2}</span>
        </Label>
        <ToggleSelection />
      </ButtonsContainer>
    </Container>
  )
}
